import React, { useState, useEffect } from 'react';
import { Shield, AlertCircle, FileText, Scale, Phone, Mail, Download, Search, ChevronDown, ChevronUp, ExternalLink, CheckCircle } from 'lucide-react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import SMSQuestionnaireService from '../../services/smsQuestionnaireService';
import Icon from '../../components/AppIcon';


const LegalDisclaimerAndCompliancePage = () => {
  const [legalDocument, setLegalDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadLegalDocument();
  }, []);

  const loadLegalDocument = async () => {
    try {
      setLoading(true);
      const documents = await SMSQuestionnaireService?.getLegalDocuments();
      const disclaimer = documents?.find(doc => doc?.document_type === 'disclaimer_and_compliance');
      
      if (disclaimer) {
        setLegalDocument(disclaimer);
      } else {
        setError('Legal disclaimer document not found');
      }
    } catch (err) {
      console.error('Failed to load legal document:', err);
      setError('Failed to load legal document. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev?.[sectionId]
    }));
  };

  const sections = [
    { id: 'introduction', title: 'Introduction and Purpose', icon: FileText },
    { id: 'definitions', title: 'Definitions and Key Terms', icon: FileText },
    { id: 'scope', title: 'Scope of Services', icon: Scale },
    { id: 'no-legal-advice', title: 'No Legal Advice or Representation', icon: AlertCircle },
    { id: 'liability', title: 'Limitations of Liability', icon: Shield },
    { id: 'client-responsibilities', title: 'Client Responsibilities', icon: CheckCircle },
    { id: 'forfeiture', title: 'Bond Forfeiture & Financial Consequences', icon: AlertCircle },
    { id: 'co-signer', title: 'Co-signer and Indemnitor Obligations', icon: FileText },
    { id: 'regulatory', title: 'California Regulatory Compliance', icon: Scale },
    { id: 'privacy', title: 'Privacy Policy', icon: Shield },
    { id: 'terms', title: 'Terms of Service', icon: FileText },
    { id: 'ada', title: 'ADA Compliance', icon: CheckCircle },
    { id: 'technology', title: 'Technology and AI Assistance Disclaimers', icon: AlertCircle },
    { id: 'tcpa', title: 'TCPA Compliance', icon: Phone },
    { id: 'dispute', title: 'Dispute Resolution', icon: Scale },
    { id: 'contact', title: 'Contact Information', icon: Mail }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading legal document...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Error Loading Document</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={loadLegalDocument}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!legalDocument) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Document Not Available</h2>
          <p className="text-gray-600">The legal disclaimer document is currently unavailable.</p>
        </div>
      </div>
    );
  }

  const filteredSections = sections?.filter(section =>
    section?.title?.toLowerCase()?.includes(searchTerm?.toLowerCase())
  );

  return (
    <>
      <Helmet>
        <title>Legal Disclaimer & Compliance | Sailors Bail Bonds | California State & Federal Courts</title>
        <meta name="description" content="Comprehensive legal disclaimer and compliance document for Sailors Bail Bonds covering liability limitations, regulatory disclosures, ADA compliance, TCPA requirements, and California bail bond industry regulations." />
        <meta name="keywords" content="legal disclaimer, bail bond compliance, liability limitations, ADA compliance, TCPA regulations, California bail bonds, legal notices, regulatory compliance" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://sailorsbail.com/legal-disclaimer-and-compliance" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        {/* Header Section */}
        <div className="bg-gradient-to-r from-blue-900 to-blue-700 text-white py-16 px-4">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center justify-center mb-6">
              <Shield className="w-16 h-16 mr-4" aria-hidden="true" />
              <div>
                <h1 className="text-4xl md:text-5xl font-black mb-2">
                  Legal Disclaimer & Compliance
                </h1>
                <p className="text-xl text-blue-100">
                  Comprehensive Legal Information and Regulatory Disclosures
                </p>
              </div>
            </div>
            
            {/* Document Info */}
            <div className="bg-blue-800/50 rounded-2xl p-6 max-w-3xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-center">
                <div>
                  <p className="text-sm text-blue-200 mb-1">Version</p>
                  <p className="text-lg font-bold">{legalDocument?.version}</p>
                </div>
                <div>
                  <p className="text-sm text-blue-200 mb-1">Effective Date</p>
                  <p className="text-lg font-bold">
                    {new Date(legalDocument?.effective_date)?.toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Critical Notice Banner */}
        <div className="max-w-6xl mx-auto px-4 -mt-10">
          <div className="bg-red-100 border-2 border-red-300 rounded-2xl p-6 shadow-xl">
            <div className="flex items-start">
              <AlertCircle className="w-8 h-8 text-red-600 mr-4 flex-shrink-0 mt-1" aria-hidden="true" />
              <div>
                <h2 className="text-2xl font-bold text-red-900 mb-3">
                  Critical Legal Notice
                </h2>
                <p className="text-red-800 mb-3 leading-relaxed">
                  <strong>PLEASE READ CAREFULLY:</strong> This comprehensive document contains essential legal disclaimers, 
                  liability limitations, client responsibilities, and regulatory compliance information specific to bail bond 
                  services. By engaging our services, you acknowledge and agree to all terms, conditions, and limitations 
                  outlined in this document.
                </p>
                <p className="text-red-700 font-semibold">
                  ⚠️ We strongly recommend consulting with a licensed attorney for legal advice specific to your situation. 
                  Sailors Bail Bonds does NOT provide legal representation or advice.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-6xl mx-auto px-4 py-12">
          {/* Quick Actions */}
          <div className="flex flex-col sm:flex-row gap-4 justify-between mb-8">
            <div className="relative flex-1 max-w-md">
              <input
                type="text"
                placeholder="Search sections..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e?.target?.value)}
                className="w-full pl-10 pr-4 py-3 border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:ring-0"
                aria-label="Search legal document sections"
              />
              <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" aria-hidden="true" />
            </div>
            
            <div className="flex gap-4">
              <button
                onClick={() => window.print()}
                className="flex items-center bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-xl font-bold transition-colors shadow-lg"
                aria-label="Download or print legal document"
              >
                <Download className="w-5 h-5 mr-2" aria-hidden="true" />
                Download PDF
              </button>
            </div>
          </div>

          {/* Table of Contents */}
          <div className="bg-white rounded-2xl shadow-lg border-2 border-gray-200 p-6 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
              <FileText className="w-6 h-6 mr-2" aria-hidden="true" />
              Table of Contents
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {filteredSections?.map((section, index) => {
                const Icon = section?.icon;
                return (
                  <button
                    key={section?.id}
                    onClick={() => {
                      toggleSection(section?.id);
                      document?.getElementById(section?.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }}
                    className="flex items-center text-left p-3 rounded-lg hover:bg-blue-50 transition-colors"
                  >
                    <Icon className="w-5 h-5 text-blue-600 mr-3 flex-shrink-0" aria-hidden="true" />
                    <span className="text-sm font-medium text-gray-700">
                      {index + 1}. {section?.title}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Content Sections */}
          <div className="space-y-6">
            {filteredSections?.map((section) => {
              const Icon = section?.icon;
              const isExpanded = expandedSections?.[section?.id];
              
              return (
                <div
                  key={section?.id}
                  id={section?.id}
                  className="bg-white rounded-2xl shadow-lg border-2 border-gray-200 overflow-hidden transition-all"
                >
                  <button
                    onClick={() => toggleSection(section?.id)}
                    className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors"
                    aria-expanded={isExpanded}
                    aria-controls={`section-content-${section?.id}`}
                  >
                    <div className="flex items-center flex-1">
                      <div className="bg-gradient-to-br from-blue-100 to-blue-200 rounded-xl p-3 mr-4">
                        <Icon className="w-6 h-6 text-blue-600" aria-hidden="true" />
                      </div>
                      <h3 className="text-xl font-bold text-gray-900">
                        {section?.title}
                      </h3>
                    </div>
                    {isExpanded ? (
                      <ChevronUp className="w-6 h-6 text-gray-400" aria-hidden="true" />
                    ) : (
                      <ChevronDown className="w-6 h-6 text-gray-400" aria-hidden="true" />
                    )}
                  </button>

                  {isExpanded && (
                    <div 
                      id={`section-content-${section?.id}`}
                      className="px-6 pb-6 bg-gray-50 border-t-2 border-gray-200"
                    >
                      <div className="prose prose-blue max-w-none">
                        <div 
                          className="whitespace-pre-wrap font-sans text-base text-gray-700 leading-relaxed"
                          dangerouslySetInnerHTML={{ 
                            __html: legalDocument?.content
                              ?.split(`<a name="${section?.id}"></a>`)?.[1]
                              ?.split('<a name=')?.[0]
                              ?.trim() || 'Content not available for this section.'
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Contact Information Card */}
          <div className="mt-12 bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-8 border-2 border-blue-200">
            <div className="text-center">
              <h2 className="text-3xl font-black text-gray-900 mb-6">
                Questions About This Legal Document?
              </h2>
              <p className="text-xl text-gray-700 mb-8 max-w-2xl mx-auto">
                If you have questions about any of the terms, limitations, or disclosures in this document, 
                please contact us. We're here to help clarify our legal obligations and your rights.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="tel:9162563468"
                  className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-8 py-4 rounded-xl font-bold text-lg transition-all transform hover:scale-105 shadow-lg flex items-center justify-center"
                >
                  <Phone className="w-5 h-5 mr-2" aria-hidden="true" />
                  Call (916) 256-3468
                </a>
                <Link
                  to="/contact-us"
                  className="bg-white border-2 border-blue-600 text-blue-600 hover:bg-blue-50 px-8 py-4 rounded-xl font-bold text-lg transition-all flex items-center justify-center"
                >
                  Contact Us Online
                  <ExternalLink className="w-5 h-5 ml-2" aria-hidden="true" />
                </Link>
              </div>
            </div>
          </div>

          {/* Related Documents */}
          <div className="mt-8 text-center">
            <p className="text-gray-600 mb-4">Related Legal Documents:</p>
            <div className="flex flex-wrap justify-center gap-6">
              <Link 
                to="/privacy-policy"
                className="text-blue-600 hover:text-blue-800 font-semibold hover:underline flex items-center"
              >
                Privacy Policy
                <ExternalLink className="w-4 h-4 ml-1" aria-hidden="true" />
              </Link>
              <Link 
                to="/terms-and-conditions"
                className="text-blue-600 hover:text-blue-800 font-semibold hover:underline flex items-center"
              >
                Terms of Service
                <ExternalLink className="w-4 h-4 ml-1" aria-hidden="true" />
              </Link>
              <Link 
                to="/legal-disclaimer"
                className="text-blue-600 hover:text-blue-800 font-semibold hover:underline flex items-center"
              >
                Service Disclaimer
                <ExternalLink className="w-4 h-4 ml-1" aria-hidden="true" />
              </Link>
            </div>
          </div>

          {/* Last Updated */}
          <div className="mt-12 text-center text-gray-500 text-sm">
            <p>
              Last Updated: {new Date(legalDocument?.updated_at)?.toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </p>
            <p className="mt-2">
              Sailors Bail Bonds | 901 H Street, Suite 103, Sacramento, CA 95814 | License #1BG43697
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default LegalDisclaimerAndCompliancePage;